// The bookkeeping behind App's account settings (useAccountSettingsControl in
// ./account-settings.tsx) as one pure reducer and a few pure questions about
// its state. The hook keeps a state in a ref, dispatches an action at each
// step and runs the effects itself (React state, the theme, GET and PUT
// /api/v1/settings); every decision about what a step may do is made here.
//
// The state holds three things:
//   - `taken`: every update_my_settings call this page took up, with what this
//     device showed before it when the server held no theme yet (the prior
//     Undo restores; undoPatch), or null;
//   - `openSave`: the theme save still in flight, if any;
//   - `revision`: moves on every value this page adopts or chooses, so a server
//     read that began before one of them is known to be older than it.
import type { ThemeChoice } from '@/settings';
import type { ToolResultOrigin } from './workspace-chat-transport';

export interface SettingsControlState {
  readonly taken: ReadonlyMap<string, ThemeChoice | null>;
  /** The number of the theme save still open, or null. */
  readonly openSave: number | null;
  /** How many theme saves this page has started (numbers them). */
  readonly saves: number;
  readonly revision: number;
}

export const INITIAL_SETTINGS_CONTROL: SettingsControlState = {
  taken: new Map(),
  openSave: null,
  saves: 0,
  revision: 0,
};

export type SettingsControlAction =
  /** Server settings (hydrate, a re-read, Undo's answer) or a live change were adopted. */
  | { type: 'adopted' }
  /** A theme was chosen here; `saving` when it is also being saved to the account. */
  | { type: 'chose'; saving: boolean }
  /** Theme save number `save` finished, saved or not. */
  | { type: 'saveSettled'; save: number }
  /** A tool call's change was taken up, with Undo's prior (or null). */
  | { type: 'took'; toolCallId: string; prior: ThemeChoice | null };

const took = (state: SettingsControlState, toolCallId: string, prior: ThemeChoice | null) => {
  const taken = new Map(state.taken);
  taken.set(toolCallId, prior);
  return { ...state, taken };
};

export function settingsControlReducer(
  state: SettingsControlState,
  action: SettingsControlAction,
): SettingsControlState {
  switch (action.type) {
    case 'adopted':
      // Anything newer than an open save replaces it: its failure no longer
      // puts the device back.
      return { ...state, revision: state.revision + 1, openSave: null };
    case 'chose':
      return action.saving
        ? {
            ...state,
            revision: state.revision + 1,
            saves: state.saves + 1,
            openSave: state.saves + 1,
          }
        : { ...state, revision: state.revision + 1 };
    case 'saveSettled':
      return {
        ...state,
        revision: state.revision + 1,
        openSave: state.openSave === action.save ? null : state.openSave,
      };
    case 'took':
      return took(state, action.toolCallId, action.prior);
  }
}

/** The part of a settings change these decisions read. */
export interface ThemeChangeFacts {
  changed: readonly string[];
  previous: { theme?: ThemeChoice | null };
  settings: { theme: ThemeChoice | null };
}

/** A change that set a theme the server did not hold before (null). */
export const setsFirstTheme = (change: ThemeChangeFacts) =>
  change.changed.includes('theme') && !change.previous.theme;

/**
 * What to do with a tool call's change that reached this page:
 *   - `ignore` it when it is another account's, when it wrote nothing (an
 *     app-language proposal alone: only the person's Apply writes that), or a
 *     call this page already took up (a resumed stream replays the turn from
 *     its start, and applying it again would overwrite a newer choice and what
 *     Undo puts back);
 *   - `reread` the server when it arrives first on a resumed stream (after a
 *     reload): the tool's snapshot may be older than the server's settings (an
 *     Undo since, a choice on another device);
 *   - apply it `live` otherwise, recording what this device showed before a
 *     first theme change as Undo's prior.
 */
export type ChangePlan =
  | { kind: 'ignore' }
  | { kind: 'reread' }
  | { kind: 'live'; prior: ThemeChoice | null };

export function planChange(
  state: SettingsControlState,
  change: ThemeChangeFacts,
  origin: ToolResultOrigin,
  { own, shown }: { own: boolean; shown: ThemeChoice },
): ChangePlan {
  if (!own || change.changed.length === 0 || state.taken.has(origin.toolCallId))
    return { kind: 'ignore' };
  if (origin.resumed) return { kind: 'reread' };
  return { kind: 'live', prior: setsFirstTheme(change) ? shown : null };
}

/**
 * Undo's prior learned from the re-read that answered a resumed change: what
 * this device showed, but only when the server still holds the change and
 * adopting it moves this device. A device that already shows the new theme
 * shows the change itself, not what came before it.
 */
export function priorAfterReread(
  change: ThemeChangeFacts,
  fresh: { theme: ThemeChoice | null },
  shown: ThemeChoice,
): ThemeChoice | null {
  const moves = fresh.theme === change.settings.theme && shown !== fresh.theme;
  return setsFirstTheme(change) && moves ? shown : null;
}

/** Whether a server read that began at `startedAt` may still be adopted: nothing moved here since, and no theme save is open. */
export const readStillFresh = (state: SettingsControlState, startedAt: number) =>
  state.revision === startedAt && state.openSave === null;

/** Whether a failed save may still put the device back: it is the save still open. */
export const saveStillOpen = (state: SettingsControlState, save: number) => state.openSave === save;

/** The prior Undo restores for a tool call's change (undoPatch), or null. */
export const undoPrior = (state: SettingsControlState, toolCallId: string | undefined) =>
  (toolCallId && state.taken.get(toolCallId)) || null;
